import type { QueryFunctionContext } from '@tanstack/react-query';
import { gql } from '@/utilities/gql';
import type { ResultOf } from '@/utilities/gql';
import { gqlClient } from '@/utilities/graphQLClient';
import { addYears, format, startOfMonth, subYears } from 'date-fns';

const QUERY = gql(`
  query Company(
    $canonicalUrl: String
    $targetEndDate: String!
    $targetStartDate: String!
  ) {
    Company(canonicalURL: $canonicalUrl) {
      priceTargets(endDate: $targetEndDate, startDate: $targetStartDate) {
        date
        targetPriceMean
        targetPriceMax
        targetPriceMin
        targetPriceMinusStdDev
        targetPricePlusStdDev
        analystCount
        dispersion
      }
    }
  }
`);

type Result = ResultOf<typeof QUERY>;
type PriceTarget = Result['Company']['priceTargets'][number];

interface AnalystData {
  dispersion: number;
  analystCount: number | null;
  priceTargetMin: number | null;
  priceTargetMax: number | null;
}

export interface PriceTargetData {
  priceTargetData: number[][];
  priceTargetKeys: number[];
  priceTargetRangeHigh: number[][];
  priceTargetRangeLow: number[][];
  analystData: Record<number, AnalystData>;
  priceTargetMin: number;
  priceTargetMax: number;
}

export const getDefaultResponse = (): PriceTargetData => ({
  priceTargetData: [],
  priceTargetKeys: [],
  priceTargetRangeHigh: [],
  priceTargetRangeLow: [],
  analystData: {},
  priceTargetMin: 0,
  priceTargetMax: 0,
});

export function getQueryKey(lookup: string): [string, string] {
  return ['price-target', lookup];
}

const analystDataReducer = (data: PriceTarget[] = []): PriceTargetData => {
  const cleanedData = data.filter(
    (entry) =>
      entry.targetPriceMinusStdDev !== null &&
      entry.targetPricePlusStdDev !== null &&
      entry.dispersion !== null
  ) as (Omit<
    PriceTarget,
    'targetPricePlusStdDev' | 'targetPriceMinusStdDev' | 'dispersion'
  > & {
    targetPriceMinusStdDev: number;
    targetPricePlusStdDev: number;
    dispersion: number;
  })[];

  const [
    priceTargetList,
    priceTargetData,
    analystData,
    priceTargetRangeHigh,
    priceTargetRangeLow,
  ] = cleanedData.reduce<
    [
      number[],
      number[][],
      PriceTargetData['analystData'],
      number[][],
      number[][],
    ]
  >(
    ([l, d, a, high, low], b) => {
      if (b.date === null || b.targetPriceMean === null)
        return [l, d, a, high, low];
      const list = [
        ...l,
        b.targetPriceMean,
        b.targetPriceMinusStdDev,
        b.targetPricePlusStdDev,
      ];
      const data = [...d, [b.date, b.targetPriceMean]];
      const analystData = {
        ...a,
        [b.date]: {
          dispersion: b.dispersion,
          analystCount: b.analystCount,
          priceTargetMin: b.targetPriceMin,
          priceTargetMax: b.targetPriceMax,
        },
      };
      const targetRangeHigh = [...high, [b.date, b.targetPricePlusStdDev]];
      const targetRangeLow = [...low, [b.date, b.targetPriceMinusStdDev]];
      return [list, data, analystData, targetRangeHigh, targetRangeLow];
    },
    [[], [], {}, [], []]
  );
  const priceTargetMin = Math.min(...priceTargetList);
  const priceTargetMax = Math.max(...priceTargetList);
  const priceTargetKeys = priceTargetData.map((item) => item[0]);
  return {
    priceTargetData,
    priceTargetKeys,
    priceTargetRangeHigh,
    priceTargetRangeLow,
    analystData,
    priceTargetMin,
    priceTargetMax,
  };
};

export const queryPriceTargets = async ({
  queryKey: [_key, canonicalUrl],
}: QueryFunctionContext<[string, string]>) => {
  const today = new Date();
  const firstOfMonth = startOfMonth(today).getTime();
  const targetStartDate = format(subYears(firstOfMonth, 1), 'yyyy-MM-dd');
  const targetEndDate = format(addYears(today, 1), 'yyyy-MM-dd');

  const params = {
    canonicalUrl,
    targetStartDate,
    targetEndDate,
  };

  const {
    Company: { priceTargets },
  } = await gqlClient.request(QUERY, params);

  return {
    ...analystDataReducer(priceTargets),
  };
};
